/**
 * MasteryRadar — the student's grades: 6 weighted grading categories drawn as a
 * radar, plus cumulative GPA, letter grade and per-subject grades.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Hexagon, Loader2 } from 'lucide-react';
import api from '../services/api';
import { RadarChart } from './gradebook/RadarChart';
import { GpaHeadline, SubjectGrades, CategoryList } from './gradebook/GradeSummary';
import { authHeaders } from './gradebook/gradeUtils';

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={`px-2.5 py-1 text-[9px] font-black tracking-widest uppercase border-2 transition-colors ${active ? 'bg-emerald-500 border-emerald-500 text-black' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={testid}>{children}</button>
);

const MasteryRadar = () => {
  const [unit, setUnit] = useState(null);
  const [subject, setSubject] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/school/gradebook${unit ? `?unit=${unit}` : ''}`, { headers: authHeaders() })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [unit]);

  const summary = data?.summary;
  const averages = useMemo(() => {
    if (!summary) return {};
    if (subject === 'all') return Object.fromEntries(summary.categories.map((c) => [c.key, c.average]));
    const s = summary.subjects.find((x) => x.key === subject);
    return Object.fromEntries((s?.categories || []).map((c) => [c.key, c.average]));
  }, [summary, subject]);
  const axes = (data?.categories || []).map((c) => ({ key: c.key, label: c.short, value: averages[c.key] ?? null }));

  return (
    <div className="mb-6 border-2 border-emerald-500/30 p-5" data-testid="mastery-radar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2"><Hexagon className="w-4 h-4 text-emerald-400" /><h3 className="text-sm font-black tracking-[0.2em] uppercase">Mastery Radar</h3></div>
        <div className="flex flex-wrap gap-1.5" data-testid="radar-unit-toggle">
          <Chip active={!unit} onClick={() => setUnit(null)} testid="radar-unit-all">Cumulative</Chip>
          {(summary?.units || []).map((u) => <Chip key={u} active={unit === u} onClick={() => setUnit(u)} testid={`radar-unit-${u}`}>Unit {u}</Chip>)}
        </div>
      </div>
      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>
      ) : !summary ? (
        <div className="text-xs text-zinc-500 py-10 text-center">Grades unavailable right now.</div>
      ) : (
        <>
          <GpaHeadline summary={summary} />
          {!summary.has_grades && <div className="text-[11px] text-zinc-500 mt-3" data-testid="radar-empty">No grades posted yet — your radar fills in as your teacher enters scores.</div>}
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-5 mt-4">
            <div className="flex flex-col items-center">
              <RadarChart axes={axes} />
              <div className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500" data-testid="radar-subject-label">
                {subject === 'all' ? 'All Subjects' : summary.subjects.find((s) => s.key === subject)?.label} · center 50% → edge 100%
              </div>
            </div>
            <div className="w-full space-y-4">
              <SubjectGrades summary={summary} active={subject} onSelect={setSubject} />
              <CategoryList categories={data.categories} averages={averages} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MasteryRadar;
